import React, { useEffect, useRef, useCallback, useState } from 'react';
import { CardData } from '../types';
import { CallIcon, WhatsAppIcon, DirectionsIcon } from './Icons';

interface GeocodedLocation {
    lat: number;
    lng: number;
    address: string;
    timestamp: number;
}

// Map pin colors: default warm saffron orange, highlighted vibrant royal blue when selected
const DEFAULT_PIN_COLOR = '#EA580C';
const SELECTED_PIN_COLOR = '#2563EB';

// Cache management utilities
const GEOCODE_CACHE_KEY = 'gujaratis_hub_geocode_cache';
const CACHE_EXPIRY_DAYS = 30;

// Pre-seeded known coordinates for Bangalore localities
const PRE_SEEDED_COORDINATES: Record<string, { lat: number; lng: number }> = {
    'marathahalli': { lat: 12.9591, lng: 77.6974 },
    'koramangala': { lat: 12.9352, lng: 77.6245 },
    'varthur road': { lat: 12.9406, lng: 77.7471 },
    'varthur': { lat: 12.9406, lng: 77.7471 },
    'horamavu': { lat: 13.0270, lng: 77.6575 },
    'kasavanahalli': { lat: 12.8995, lng: 77.6749 },
    'kasavahahalli': { lat: 12.8995, lng: 77.6749 },
    'sarajapur road': { lat: 12.8995, lng: 77.6749 },
    'bellandur': { lat: 12.9304, lng: 77.6784 },
    'prakash nagar': { lat: 12.9904, lng: 77.5530 },
    'rajaji nagar': { lat: 12.9982, lng: 77.5530 },
    'rajani nagar': { lat: 12.9982, lng: 77.5530 },
    'whitefield': { lat: 12.9698, lng: 77.7500 },
    'kadugodi': { lat: 12.9980, lng: 77.7600 },
    'btm layout': { lat: 12.9166, lng: 77.6101 },
    'btm': { lat: 12.9166, lng: 77.6101 },
    'munnekolal': { lat: 12.9569, lng: 77.7126 },
    'silver spring': { lat: 12.9569, lng: 77.7126 },
    'hoodi': { lat: 12.9918, lng: 77.7169 },
    'seegehalli': { lat: 12.9980, lng: 77.7600 },
    'sarjapur road': { lat: 12.9237, lng: 77.6548 },
    'bengaluru': { lat: 12.9716, lng: 77.5946 },
    'bangalore': { lat: 12.9716, lng: 77.5946 },
};

const getGeocodeCacheKey = (address: string): string => {
    return address.toLowerCase().trim().replace(/\s+/g, ' ');
};

const getFromCache = (address: string): GeocodedLocation | null => {
    const cacheKey = getGeocodeCacheKey(address);

    try {
        const cache = localStorage.getItem(GEOCODE_CACHE_KEY);
        if (cache) {
            const parsedCache = JSON.parse(cache);
            const cached = parsedCache[cacheKey];

            if (cached) {
                const now = Date.now();
                const expiryTime = cached.timestamp + (CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
                if (now <= expiryTime) {
                    return cached;
                }
            }
        }
    } catch (error) {
        console.warn('Error reading geocode cache:', error);
    }

    for (const [locality, coords] of Object.entries(PRE_SEEDED_COORDINATES)) {
        if (cacheKey.includes(locality)) {
            const preSeeded: GeocodedLocation = {
                lat: coords.lat,
                lng: coords.lng,
                address: cacheKey,
                timestamp: Date.now(),
            };
            saveToCache(address, coords.lat, coords.lng);
            return preSeeded;
        }
    }

    return null;
};

const saveToCache = (address: string, lat: number, lng: number): void => {
    try {
        const cache = localStorage.getItem(GEOCODE_CACHE_KEY);
        const parsedCache = cache ? JSON.parse(cache) : {};
        const cacheKey = getGeocodeCacheKey(address);

        parsedCache[cacheKey] = {
            lat,
            lng,
            address: cacheKey,
            timestamp: Date.now(),
        };

        localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(parsedCache));
    } catch (error) {
        console.warn('Error saving to geocode cache:', error);
    }
};

const clearExpiredCache = (): void => {
    try {
        const cache = localStorage.getItem(GEOCODE_CACHE_KEY);
        if (!cache) return;

        const parsedCache = JSON.parse(cache);
        const now = Date.now();
        let hasChanges = false;

        Object.keys(parsedCache).forEach(key => {
            const cached = parsedCache[key];
            const expiryTime = cached.timestamp + (CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);

            if (now > expiryTime) {
                delete parsedCache[key];
                hasChanges = true;
            }
        });

        if (hasChanges) {
            localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(parsedCache));
        }
    } catch (error) {
        console.warn('Error clearing expired cache:', error);
    }
};

// Generate custom SVG pin marker with uniform or highlighted styling
const createPinIcon = (color: string = DEFAULT_PIN_COLOR, isSelected: boolean = false) => {
    const width = isSelected ? 38 : 32;
    const height = isSelected ? 48 : 40;
    const centerRadius = isSelected ? 6.5 : 5;

    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
            <defs>
                <filter id="shadow" x="-25%" y="-15%" width="150%" height="150%">
                    <feDropShadow dx="0" dy="${isSelected ? 3 : 2}" stdDeviation="${isSelected ? 3 : 2}" flood-color="#000000" flood-opacity="${isSelected ? 0.45 : 0.3}"/>
                </filter>
            </defs>
            <path d="M${width / 2} 0 C${(width / 2) - 9} 0 0 7.16 0 16 C0 26 ${width / 2} ${height} ${width / 2} ${height} C${width / 2} ${height} ${width} 26 ${width} 16 C${width} 7.16 ${(width / 2) + 9} 0 ${width / 2} 0 Z" 
                  fill="${color}" 
                  filter="url(#shadow)"
                  stroke="#FFFFFF" 
                  stroke-width="${isSelected ? 2.5 : 1.5}"/>
            <circle cx="${width / 2}" cy="${isSelected ? 18 : 15}" r="${centerRadius}" fill="#FFFFFF"/>
            ${isSelected ? `<circle cx="${width / 2}" cy="18" r="3.5" fill="${color}"/>` : ''}
        </svg>
    `;

    return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
        scaledSize: new google.maps.Size(width, height),
        anchor: new google.maps.Point(width / 2, height),
    };
};

const sanitizeValue = (val?: string): string => {
    if (!val) return '';
    const trimmed = val.trim();
    if (trimmed === '-' || trimmed === '--' || trimmed === 'N/A' || trimmed === 'na') return '';
    return trimmed;
};

const getCardKey = (card: CardData): string => {
    if (card.id) return card.id;
    return `${card.name}_${card.businessName || ''}_${card.category || ''}`;
};

const parsePhoneNumbers = (phoneStr?: string): string[] => {
    if (!phoneStr) return [];
    const parts = phoneStr.split(/[,/;|]+/);
    return parts
        .map(p => p.trim())
        .filter(p => p.length > 0 && p !== '-' && p !== '--' && p !== 'N/A');
};

const getCleanPhoneDigits = (phone?: string): string => {
    if (!phone) return '';
    return phone.replace(/\D/g, '');
};

const getWhatsAppUrl = (phone?: string): string => {
    const digits = getCleanPhoneDigits(phone);
    if (!digits) return '';
    const waNumber = digits.length === 10 ? `91${digits}` : digits;
    return `https://wa.me/${waNumber}`;
};

const getDirectionsUrl = (card: CardData): string => {
    const mapLoc = sanitizeValue(card.googleMapLocation);
    if (mapLoc && (mapLoc.startsWith('http://') || mapLoc.startsWith('https://'))) {
        return mapLoc;
    }
    const cleanAddr = sanitizeValue(card.fullAddress) || sanitizeValue(card.area);
    const cleanTitle = sanitizeValue(card.businessName) || sanitizeValue(card.name);
    const query = [cleanTitle, cleanAddr, 'Bengaluru'].filter(Boolean).join(', ');
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`;
};

export interface MapViewProps {
    cards: CardData[];
    selectedCard: CardData | null;
    onMarkerClick: (card: CardData | null) => void;
    totalCategoriesCount?: number;
    className?: string;
}

export const MapView: React.FC<MapViewProps> = ({ 
    cards, 
    selectedCard, 
    onMarkerClick, 
    totalCategoriesCount = 8,
    className = '',
}) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<google.maps.Map | null>(null);
    const markersRef = useRef<google.maps.Marker[]>([]);
    const cardMarkersRef = useRef<Map<string, google.maps.Marker>>(new Map());
    const previousSelectedKeyRef = useRef<string | null>(null);
    const [mapError, setMapError] = useState<string | null>(null);
    const [isApiLoading, setIsApiLoading] = useState(true);

    const onMarkerClickRef = useRef(onMarkerClick);
    useEffect(() => {
        onMarkerClickRef.current = onMarkerClick;
    }, [onMarkerClick]);

    const selectedCardRef = useRef(selectedCard);
    useEffect(() => {
        selectedCardRef.current = selectedCard;
    }, [selectedCard]);

    const coordSeenCount = useRef<Map<string, number>>(new Map());

    // addMarkers runs strictly when the cards list changes
    const addMarkers = useCallback(() => {
        if (!mapInstanceRef.current || !window.google) return;

        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];
        cardMarkersRef.current.clear();
        coordSeenCount.current.clear();

        const geocoder = new google.maps.Geocoder();
        const bounds = new google.maps.LatLngBounds();
        let markersProcessed = 0;
        const totalCards = cards.length;

        clearExpiredCache();

        cards.forEach((card) => {
            const validFull = sanitizeValue(card.fullAddress);
            const validArea = sanitizeValue(card.area);
            const addressToGeocode = validFull || validArea || '';

            if (!addressToGeocode) {
                markersProcessed++;
                return;
            }

            const cachedLocation = getFromCache(addressToGeocode);
            const cardKey = getCardKey(card);

            const createMarker = (baseLat: number, baseLng: number) => {
                const coordKey = `${baseLat.toFixed(3)}_${baseLng.toFixed(3)}`;
                const seenCount = coordSeenCount.current.get(coordKey) || 0;
                coordSeenCount.current.set(coordKey, seenCount + 1);

                let lat = baseLat;
                let lng = baseLng;
                if (seenCount > 0) {
                    const angle = (seenCount * 137.5 * Math.PI) / 180;
                    const offset = 0.00018 * Math.sqrt(seenCount);
                    lat += Math.cos(angle) * offset;
                    lng += Math.sin(angle) * offset;
                }

                const position = new google.maps.LatLng(lat, lng);

                const isSelected = selectedCardRef.current && getCardKey(selectedCardRef.current) === cardKey;
                const marker = new google.maps.Marker({
                    position,
                    map: mapInstanceRef.current,
                    title: card.businessName || card.name,
                    icon: createPinIcon(isSelected ? SELECTED_PIN_COLOR : DEFAULT_PIN_COLOR, !!isSelected),
                    zIndex: isSelected ? 1000 : 10,
                });
                if (isSelected) {
                    previousSelectedKeyRef.current = cardKey;
                }

                marker.addListener('click', () => {
                    onMarkerClickRef.current(card);
                });

                markersRef.current.push(marker);
                cardMarkersRef.current.set(cardKey, marker);
                bounds.extend(position);

                markersProcessed++;

                if (markersProcessed === totalCards && !bounds.isEmpty()) {
                    mapInstanceRef.current?.fitBounds(bounds, { top: 50, right: 50, bottom: 80, left: 50 });
                }
            };

            if (cachedLocation) {
                createMarker(cachedLocation.lat, cachedLocation.lng);
            } else {
                geocoder.geocode({ address: addressToGeocode }, (results, status) => {
                    if (status === 'OK' && results && results[0]) {
                        const position = results[0].geometry.location;
                        const lat = position.lat();
                        const lng = position.lng();
                        saveToCache(addressToGeocode, lat, lng);
                        createMarker(lat, lng);
                    } else {
                        markersProcessed++;
                    }
                });
            }
        });
    }, [cards]);

    // When user selects any location: smooth pan, move map so location is on the left, and highlight pin with different color
    useEffect(() => {
        if (!mapInstanceRef.current || cardMarkersRef.current.size === 0) return;

        // Reset previous selected marker back to default color & z-index
        if (previousSelectedKeyRef.current) {
            const prevMarker = cardMarkersRef.current.get(previousSelectedKeyRef.current);
            if (prevMarker) {
                prevMarker.setIcon(createPinIcon(DEFAULT_PIN_COLOR, false));
                prevMarker.setZIndex(10);
            }
            previousSelectedKeyRef.current = null;
        }

        if (selectedCard) {
            const currentKey = getCardKey(selectedCard);
            const selectedMarker = cardMarkersRef.current.get(currentKey);

            if (selectedMarker) {
                // Highlight with distinct color and higher zIndex
                selectedMarker.setIcon(createPinIcon(SELECTED_PIN_COLOR, true));
                selectedMarker.setZIndex(1000);
                previousSelectedKeyRef.current = currentKey;

                const position = selectedMarker.getPosition();
                if (position) {
                    // Center onto marker
                    mapInstanceRef.current.panTo(position);
                    const currentZoom = mapInstanceRef.current.getZoom();
                    if (!currentZoom || currentZoom < 13) {
                        mapInstanceRef.current.setZoom(13);
                    }

                    // Shift camera so marker moves to the LEFT of the screen
                    setTimeout(() => {
                        if (mapInstanceRef.current) {
                            const isMobileScreen = window.innerWidth < 768;
                            if (isMobileScreen) {
                                mapInstanceRef.current.panBy(0, -80);
                            } else {
                                mapInstanceRef.current.panBy(160, -30);
                            }
                        }
                    }, 200);
                }
            }
        }
    }, [selectedCard]);

    // Robust, instant Google Maps initialization (solves "takes lot of time / not loading" bug)
    useEffect(() => {
        let pollTimer: NodeJS.Timeout | null = null;

        const checkAndInitMap = () => {
            if (mapInstanceRef.current) return;
            if (!mapRef.current) return;
            if (!window.google?.maps) return;

            setIsApiLoading(false);
            setMapError(null);

            const isDark = document.documentElement.classList.contains('dark');
            const darkStyles = [
                { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
                { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
                { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
                { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
                { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
                { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#263c3f' }] },
                { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#6b9a76' }] },
                { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
                { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
                { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
                { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#746855' }] },
                { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2835' }] },
                { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] }
            ];

            const map = new google.maps.Map(mapRef.current, {
                zoom: 11,
                center: { lat: 12.9716, lng: 77.5946 }, // Bangalore coordinates
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                zoomControl: true,
                clickableIcons: false,
                gestureHandling: 'greedy',
                styles: isDark ? darkStyles : undefined,
                restriction: {
                    latLngBounds: {
                        north: 14.0,
                        south: 12.0,
                        east: 78.5,
                        west: 76.5,
                    },
                },
            });

            mapInstanceRef.current = map;
            addMarkers();
        };

        const loadGoogleMapsAPI = () => {
            // 1. If google maps is already loaded in window, init immediately (0ms delay)!
            if (window.google?.maps) {
                checkAndInitMap();
                return;
            }

            const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
            if (!apiKey) {
                setMapError('Google Maps API Key not set. Configure REACT_APP_GOOGLE_MAPS_API_KEY in .env.local.');
                setIsApiLoading(false);
                return;
            }

            (window as any).initMap = () => {
                checkAndInitMap();
            };

            const existingScript = document.getElementById('google-maps-script') as HTMLScriptElement | null;
            if (!existingScript) {
                const script = document.createElement('script');
                script.id = 'google-maps-script';
                script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMap&libraries=&region=IN&language=en`;
                script.async = true;
                script.defer = true;
                script.onerror = () => {
                    setMapError('Failed to load Google Maps script. Check your internet connection or API restrictions.');
                    setIsApiLoading(false);
                };
                script.onload = () => {
                    checkAndInitMap();
                };
                document.head.appendChild(script);
            } else {
                existingScript.addEventListener('load', () => checkAndInitMap());
            }

            // 3. Fallback poll in case callback was already fired before mount
            let attempts = 0;
            pollTimer = setInterval(() => {
                attempts++;
                if (window.google?.maps) {
                    if (pollTimer) clearInterval(pollTimer);
                    checkAndInitMap();
                } else if (attempts > 50) { // 5s timeout
                    if (pollTimer) clearInterval(pollTimer);
                    if (!mapInstanceRef.current) {
                        setIsApiLoading(false);
                    }
                }
            }, 100);
        };

        loadGoogleMapsAPI();

        return () => {
            if (pollTimer) clearInterval(pollTimer);
            markersRef.current.forEach(marker => marker.setMap(null));
            markersRef.current = [];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Redraw markers when cards array changes
    useEffect(() => {
        if (window.google && mapInstanceRef.current) {
            addMarkers();
        }
    }, [addMarkers]);

    // Handle container resize when panels collapse/expand
    useEffect(() => {
        const mapInstance = mapInstanceRef.current;
        if (mapInstance && window.google) {
            const timer = setTimeout(() => {
                if (mapInstance && window.google) {
                    google.maps.event.trigger(mapInstance, 'resize');
                }
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [className]);

    return (
        <div className={`relative w-full h-full min-h-[500px] rounded-3xl overflow-hidden shadow-soft-lg border border-orange-100/70 dark:border-gray-800 bg-white dark:bg-gray-900 ${className}`}>
            {/* Map Container */}
            <div ref={mapRef} className="w-full h-full min-h-[500px]"></div>

            {/* Loading Overlay */}
            {isApiLoading && (
                <div className="absolute inset-0 bg-[#FAF7F2]/80 dark:bg-[#121418]/80 backdrop-blur-sm flex flex-col items-center justify-center z-10">
                    <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Loading Bengaluru Map...</p>
                </div>
            )}

            {/* Error / Fallback State */}
            {mapError && (
                <div className="absolute inset-0 bg-gradient-to-b from-orange-50/90 to-amber-50/90 dark:from-gray-900/95 dark:to-gray-800/95 p-6 flex flex-col items-center justify-center text-center z-20">
                    <div className="w-14 h-14 rounded-2xl bg-orange-100 dark:bg-orange-950/50 text-orange-600 flex items-center justify-center text-2xl mb-3 shadow-sm">
                        🗺️
                    </div>
                    <h4 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                        Map View Preview
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 max-w-sm mb-4">
                        {mapError}
                    </p>
                    <div className="bg-white/80 dark:bg-gray-800/80 px-4 py-2.5 rounded-xl border border-orange-200/60 dark:border-gray-700 text-xs text-gray-700 dark:text-gray-300">
                        Listing locations in Marathahalli, BTM Layout, Whitefield, Kadugodi & more.
                    </div>
                </div>
            )}

            {/* Selected Card Details Overlay on Top-Right Corner of Map */}
            {selectedCard && (() => {
                const validBusiness = sanitizeValue(selectedCard.businessName);
                const validName = sanitizeValue(selectedCard.name);
                const displayName = validBusiness || validName || 'Gujarati Member';
                const secondaryName = validBusiness && validName && validBusiness !== validName ? validName : null;

                const validFullAddress = sanitizeValue(selectedCard.fullAddress);
                const validArea = sanitizeValue(selectedCard.area);
                const fullAddressDisplay = validFullAddress || validArea || 'Bengaluru, Karnataka';

                const validCategory = sanitizeValue(selectedCard.category);
                const primaryCategory = validCategory ? validCategory.split(',')[0].trim() : 'Food Services';

                const phoneNumbers = parsePhoneNumbers(selectedCard.contact);
                const primaryPhone = phoneNumbers[0] || '';
                const cleanPhoneDigits = getCleanPhoneDigits(primaryPhone);
                const whatsAppUrl = getWhatsAppUrl(primaryPhone);
                const directionsUrl = getDirectionsUrl(selectedCard);
                const comments = sanitizeValue(selectedCard.comments);

                return (
                    <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-30 w-[94%] max-w-[360px] sm:max-w-[400px] max-h-[calc(100%-2rem)] sm:max-h-[calc(100%-3rem)] overflow-y-auto shadow-2xl rounded-2xl animate-in fade-in slide-in-from-top-3 duration-250 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border border-orange-200/80 dark:border-gray-700">
                        {/* Header Banner / Top Row with Close Button */}
                        <div className="relative p-4 sm:p-5 border-b border-orange-100/80 dark:border-gray-800">
                            {/* Close Button */}
                            <button
                                onClick={() => onMarkerClick(null)}
                                aria-label="Close place details"
                                className="absolute top-3.5 right-3.5 z-40 w-8 h-8 bg-gray-100 hover:bg-orange-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-500 hover:text-orange-600 dark:text-gray-300 dark:hover:text-white rounded-full flex items-center justify-center text-sm font-bold shadow-sm transition-all duration-150 active:scale-90"
                            >
                                ✕
                            </button>

                            <div className="flex items-start gap-3.5 pr-8">
                                {/* Thumbnail / Avatar */}
                                <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl overflow-hidden bg-gradient-to-br from-amber-50 to-orange-100 dark:from-gray-800 dark:to-gray-700 border border-orange-200/60 dark:border-gray-700 flex items-center justify-center shadow-sm">
                                    {selectedCard.imageUrl ? (
                                        <img
                                            src={selectedCard.imageUrl}
                                            alt={displayName}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                            }}
                                        />
                                    ) : (
                                        <span className="w-10 h-10 rounded-full bg-gradient-to-tr from-orange-600 to-amber-500 text-white font-extrabold text-base flex items-center justify-center shadow-sm">
                                            {(displayName || 'G').charAt(0).toUpperCase()}
                                        </span>
                                    )}
                                </div>

                                <div className="min-w-0 flex-1">
                                    <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white leading-tight break-words">
                                        {displayName}
                                    </h3>
                                    {secondaryName && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5 truncate">
                                            Owner: {secondaryName}
                                        </p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-[#FFF0E5] dark:bg-orange-950/50 text-orange-700 dark:text-orange-300 border border-orange-200/60 dark:border-orange-900/40">
                                            {primaryCategory}
                                        </span>
                                        {selectedCard.isCateringServiceAvailable === 'Yes' && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#EDF8F1] dark:bg-emerald-950/40 text-[#16A34A] dark:text-emerald-300 font-medium text-[11px] border border-emerald-200/50 dark:border-emerald-900/40">
                                                <span>🍽️</span>
                                                <span>Catering</span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Body Details */}
                        <div className="p-4 sm:p-5 space-y-3.5">
                            {/* Full Address Section */}
                            <div className="bg-orange-50/40 dark:bg-gray-800/60 rounded-xl p-3 border border-orange-100/70 dark:border-gray-700/60">
                                <div className="flex items-center gap-1.5 text-xs font-semibold text-orange-600 dark:text-orange-400 mb-1">
                                    <span>📍</span>
                                    <span>Full Address</span>
                                    {validArea && (
                                        <span className="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-orange-100 dark:border-gray-600">
                                            {validArea}
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-200 leading-relaxed font-normal whitespace-pre-line">
                                    {fullAddressDisplay}
                                </p>
                            </div>

                            {/* Contact Numbers Display */}
                            {phoneNumbers.length > 0 && (
                                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 px-1">
                                    <span className="text-gray-400">📞</span>
                                    <span className="font-medium text-gray-900 dark:text-gray-100">
                                        {phoneNumbers.join(', ')}
                                    </span>
                                </div>
                            )}

                            {/* Comments / Notes if available */}
                            {comments && (
                                <div className="text-xs italic text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 rounded-xl border border-gray-100 dark:border-gray-800">
                                    💬 {comments}
                                </div>
                            )}

                            {/* Action Buttons: Call, WhatsApp, Directions */}
                            <div className="grid grid-cols-3 gap-2 pt-1">
                                {/* Call Button */}
                                {cleanPhoneDigits ? (
                                    <a
                                        href={`tel:${cleanPhoneDigits}`}
                                        className="flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-soft transition-all duration-150 active:scale-95"
                                        title={`Call ${primaryPhone}`}
                                    >
                                        <CallIcon className="w-4 h-4 flex-shrink-0" />
                                        <span>Call</span>
                                    </a>
                                ) : (
                                    <span
                                        className="flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-xl text-xs font-bold text-gray-400 bg-gray-100 dark:bg-gray-800 dark:text-gray-600 cursor-not-allowed opacity-60"
                                        title="No phone number provided"
                                    >
                                        <CallIcon className="w-4 h-4 flex-shrink-0" />
                                        <span>Call</span>
                                    </span>
                                )}

                                {/* WhatsApp Button */}
                                {whatsAppUrl ? (
                                    <a
                                        href={whatsAppUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-xl text-xs font-bold text-white bg-[#25D366] hover:bg-[#20BD5A] shadow-soft transition-all duration-150 active:scale-95"
                                        title="Chat on WhatsApp"
                                    >
                                        <WhatsAppIcon className="w-4 h-4 flex-shrink-0" />
                                        <span>WhatsApp</span>
                                    </a>
                                ) : (
                                    <span
                                        className="flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-xl text-xs font-bold text-gray-400 bg-gray-100 dark:bg-gray-800 dark:text-gray-600 cursor-not-allowed opacity-60"
                                        title="No phone number provided"
                                    >
                                        <WhatsAppIcon className="w-4 h-4 flex-shrink-0" />
                                        <span>WhatsApp</span>
                                    </span>
                                )}

                                {/* Directions Button */}
                                <a
                                    href={directionsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-xl text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-soft transition-all duration-150 active:scale-95"
                                    title="Get Directions on Google Maps"
                                >
                                    <DirectionsIcon className="w-4 h-4 flex-shrink-0" />
                                    <span>Directions</span>
                                </a>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Bottom Stats Overlay Bar */}
            <div className="absolute bottom-4 left-4 right-4 z-20 pointer-events-none">
                <div className="flex flex-col sm:flex-row items-end sm:items-center justify-between gap-3">
                    {/* Stats Card */}
                    <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md px-4 py-2.5 rounded-2xl shadow-soft-lg border border-orange-100 dark:border-gray-700 flex items-center gap-4 sm:gap-6 pointer-events-auto">
                        <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-950/50 text-orange-600 dark:text-orange-400 flex items-center justify-center text-sm font-bold">
                                👥
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-extrabold text-gray-900 dark:text-white leading-tight">
                                    {cards.length}+
                                </p>
                                <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                                    Listings
                                </p>
                            </div>
                        </div>

                        <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />

                        <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-950/50 text-red-600 dark:text-red-400 flex items-center justify-center text-sm font-bold">
                                ❤️
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-extrabold text-gray-900 dark:text-white leading-tight">
                                    {totalCategoriesCount}+
                                </p>
                                <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                                    Categories
                                </p>
                            </div>
                        </div>

                        <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 hidden xs:block" />

                        <div className="hidden xs:flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center text-sm font-bold">
                                ★
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-extrabold text-gray-900 dark:text-white leading-tight">
                                    Verified
                                </p>
                                <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                                    Directory
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Handwritten Community Note */}
                    <div className="hidden md:flex flex-col items-end pointer-events-auto bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm px-3 py-1.5 rounded-xl border border-orange-100/50 dark:border-gray-800">
                        <span className="font-handwriting text-sm lg:text-base font-bold text-gray-800 dark:text-gray-200">
                            New City, Same Community ❤️
                        </span>
                        <svg className="w-16 h-2 text-orange-400" viewBox="0 0 80 10" fill="none">
                            <path d="M0 5 Q 20 0, 40 5 T 80 5" stroke="currentColor" strokeWidth="2" strokeDasharray="3 3" fill="none" />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MapView;